import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  useTheme,
} from '@mui/material';
import { RadarChart } from '@mui/x-charts/RadarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import { BarChart } from '@mui/x-charts/BarChart';
import axios from '../../../../api/axios';
import ActivityGrid from './ActivityGrid';

/**
 * 学生题目页综合图表：雷达图 + 7天提交数量 + 每日活跃 + 各专题提交
 */
function StudentQuestionCharts({
  selectedCourse,
  totalQuestions = 0,
  totalAttempted = 0,
  totalCompleted = 0,
}) {
  const theme = useTheme();
  const [categoryData, setCategoryData] = useState(null);
  const [lineData, setLineData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const courseOfferingId = selectedCourse?.offeringId;
  const courseTemplateId = selectedCourse?.id;
  const params = useMemo(
    () => (courseOfferingId ? { courseOfferingId } : courseTemplateId ? { courseTemplateId } : {}),
    [courseOfferingId, courseTemplateId]
  );
  const hasCourse = !!(courseOfferingId || courseTemplateId);

  useEffect(() => {
    if (!hasCourse) {
      setCategoryData(null);
      setLineData(null);
      setLoading(false);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.allSettled([
      axios.get('/submissions/stats/category-performance', { params }),
      axios.get('/submissions/stats/last7days-comparison', { params }),
    ])
      .then(([catRes, lineRes]) => {
        if (cancelled) return;
        const catData = catRes.status === 'fulfilled' ? catRes.value.data : null;
        const lineDataRes = lineRes.status === 'fulfilled' ? lineRes.value.data : null;
        setCategoryData(catData);
        setLineData(lineDataRes);
        const catErr = catRes.status === 'rejected' ? catRes.reason : null;
        const lineErr = lineRes.status === 'rejected' ? lineRes.reason : null;
        if (catErr && lineErr) {
          setError(catErr?.response?.data?.error || lineErr?.response?.data?.error || '加载图表数据失败');
        } else if (catErr || lineErr) {
          setError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.response?.data?.error || err?.message || '加载图表数据失败');
          setCategoryData(null);
          setLineData(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [hasCourse, params]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  const labels = categoryData?.labels || [];
  const userData = categoryData?.currentUser?.data || [];
  const topStudentData = categoryData?.topStudent?.data || null;
  const averageData = categoryData?.averageData || null;
  const submissionCount = categoryData?.submissionCount || [];
  const topStudentSubmissionCount = categoryData?.topStudent?.submissionCount || null;
  const hasRadarData = labels.length > 0 && userData.length === labels.length;
  const hasSubmissionBarData = labels.length > 0 && submissionCount.length === labels.length;

  const hasLineData = lineData?.labels?.length > 0 && lineData?.currentUser?.data?.length > 0;

  const shortLabels = labels.map((l) =>
    l.length <= 12 ? l : l.replace(/^(\d+\.\s*)?/, '').slice(0, 10) + (l.length > 10 ? '…' : '')
  );

  const radarSeries = [
    { label: '你', data: userData.map((v) => (v != null ? v : 0)), color: theme.palette.primary.main },
    ...(topStudentData?.length === labels.length
      ? [{ label: '班级最高', data: topStudentData.map((v) => (v != null ? v : 0)), color: theme.palette.secondary.main }]
      : []),
    ...(averageData?.length === labels.length
      ? [{ label: '班级平均', data: averageData.map((v) => (v != null ? v : 0)), color: theme.palette.success.main }]
      : []),
  ];

  const lineSeries = [
    { data: lineData?.currentUser?.data || [], label: '你', color: '#4db6ac' },
    ...(lineData?.topStudent
      ? [{ data: lineData.topStudent.data, label: '班级最高', color: '#f06292' }]
      : []),
  ];

  return (
    <Box sx={{ mt: 4, mb: 3 }}>
      <Typography component="h2" variant="h5" gutterBottom fontWeight="bold" sx={{ mb: 2 }}>
        学习数据
      </Typography>
      {!hasCourse && (
        <Alert severity="info" sx={{ mb: 2 }}>
          请先在顶部导航选择班级，即可显示完整图表与班级对比。
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* 每日活跃 */}
        {hasCourse && (
          <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
            <Card variant="outlined" sx={{ flex: 1, backgroundColor: 'transparent' }}>
              <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', '&:last-child': { pb: 1.5 } }}>
                <ActivityGrid selectedCourse={selectedCourse} embedded />
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* 各专题完成率 — 雷达图 */}
        {hasRadarData && (
          <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
            <Card
              variant="outlined"
              sx={{
                flex: 1,
                backgroundColor: 'transparent',
              }}
            >
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  各专题完成率（雷达图）
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  你 vs 班级最高 vs 班级平均
                </Typography>
                <Box sx={{ width: '100%', minHeight: 300, display: 'flex', justifyContent: 'center' }}>
                  <RadarChart
                    height={280}
                    series={radarSeries}
                    radar={{ metrics: shortLabels, max: 100 }}
                    slotProps={{ legend: { hidden: false } }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* 最近 7 天提交数量 — 折线图 */}
        {hasLineData && (
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ height: '100%', backgroundColor: 'transparent' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  最近 7 天提交数量
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  每日提交次数，可与本课程全局最高同学对比
                </Typography>
                <Box sx={{ width: '100%', height: 260 }}>
                  <LineChart
                    xAxis={[{ data: lineData.labels, scaleType: 'point', id: 'days' }]}
                    series={lineSeries}
                    height={240}
                    margin={{ top: 20, right: 20, bottom: 40, left: 50 }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* 各专题提交数量 — 柱状图 */}
        {hasSubmissionBarData && (
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ height: '100%', backgroundColor: 'transparent' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  各专题提交数量
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  你在各专题的累计提交次数，可与班级最高同学对比
                </Typography>
                <Box sx={{ width: '100%', height: 260 }}>
                  <BarChart
                    xAxis={[
                      {
                        scaleType: 'band',
                        data: labels.map((l) => (l.length > 8 ? l.slice(0, 8) + '…' : l)),
                        id: 'categories',
                        categoryGapRatio: 0.7,
                        barGapRatio: 0.3,
                      },
                    ]}
                    series={[
                      { data: submissionCount, label: '我的提交', color: '#4db6ac' },
                      ...(topStudentSubmissionCount && topStudentSubmissionCount.length === labels.length
                        ? [{ data: topStudentSubmissionCount, label: '班级最高', color: '#ff9800' }]
                        : []),
                    ]}
                    height={240}
                    margin={{ top: 20, right: 20, bottom: 80, left: 50 }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* 无数据提示（仅当未选课程且无其他图表时显示） */}
        {!hasCourse && !hasRadarData && !hasLineData && !hasSubmissionBarData && (
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ backgroundColor: 'transparent' }}>
              <CardContent>
                <Typography color="text.secondary" textAlign="center" py={4}>
                  暂无统计数据，完成题目并选择课程后即可看到图表。
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

export default StudentQuestionCharts;
